import { useState, useEffect, useContext, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { 
  Shield, ChevronLeft, Download, Send, FileImage, FileVideo, 
  RefreshCw, AlertCircle, Clock, CheckCircle, FolderOpen,
  User, Mail, Phone, FileText, MessageCircle, Loader2, LogOut
} from "lucide-react";
import { Button } from "../components/ui/button";
import { AuthContext } from "../App";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  offen: { label: "Offen", color: "#A0E8AF", bgColor: "#A0E8AF20", icon: FolderOpen },
  in_bearbeitung: { label: "In Bearbeitung", color: "#F6E05E", bgColor: "#F6E05E20", icon: Clock },
  geschlossen: { label: "Geschlossen", color: "#A0AEC0", bgColor: "#A0AEC020", icon: CheckCircle }
};

const CATEGORY_CONFIG = {
  mobbing: { label: "Mobbing", color: "#FFC4D6" },
  cybermobbing: { label: "Cybermobbing", color: "#BDE0FE" },
  identitaetsdiebstahl: { label: "Identitätsdiebstahl", color: "#A0E8AF" },
  andere: { label: "Andere", color: "#F6E05E" }
};

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, logout } = useContext(AuthContext);
  
  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };
  
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  const [notes, setNotes] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [previewImage, setPreviewImage] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      setNotes(res.data.report?.notes || "");
    } catch (e) {
      console.error("Failed to fetch case", e);
      if (e.response?.status === 401) {
        toast.error("Sitzung abgelaufen. Bitte erneut anmelden.");
        logout();
        navigate("/admin/login");
      } else if (e.response?.status === 404) {
        toast.error("Fall nicht gefunden");
        navigate("/admin/dashboard");
      } else {
        toast.error("Fehler beim Laden des Falls");
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, token, logout, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (newStatus) => {
    setIsUpdating(true);
    try {
      await axios.put(`${API}/admin/reports/${id}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Status aktualisiert");
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Aktualisieren");
    } finally {
      setIsUpdating(false);
    }
  };

  const saveNotes = async () => {
    setIsUpdating(true);
    try {
      await axios.put(`${API}/admin/reports/${id}`, 
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notizen gespeichert");
    } catch (e) {
      toast.error("Fehler beim Speichern");
    } finally {
      setIsUpdating(false);
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim()) return;
    
    setIsSendingReply(true);
    try {
      await axios.post(`${API}/admin/reports/${id}/reply`, 
        { report_id: id, message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Nachricht gespeichert (E-Mail-Versand in Produktion)");
      setReplyMessage("");
      fetchData();
    } catch (e) {
      if (e.response?.data?.detail) {
        toast.error(e.response.data.detail);
      } else {
        toast.error("Fehler beim Senden");
      }
    } finally {
      setIsSendingReply(false);
    }
  };

  const exportPDF = async () => {
    setIsExporting(true);
    try {
      const res = await axios.get(`${API}/admin/reports/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fall_${id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF heruntergeladen");
    } catch (e) {
      toast.error("Fehler beim Exportieren");
    } finally {
      setIsExporting(false);
    }
  };

  const loadFilePreview = async (fileId) => {
    try {
      const res = await axios.get(`${API}/admin/files/${fileId}/base64`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewImage(res.data);
    } catch (e) {
      toast.error("Konnte Vorschau nicht laden");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unbekannt";
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[#A0E8AF] mx-auto mb-4" />
          <p className="text-[#718096]">Lade Fall...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-[#FC8181] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#2D3748] mb-2">Fall nicht gefunden</h2>
          <Link to="/admin/dashboard">
            <Button className="btn-primary">Zurück zum Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { report, files, replies } = data;
  const status = STATUS_CONFIG[report.status] || STATUS_CONFIG.offen;
  const category = CATEGORY_CONFIG[report.category] || CATEGORY_CONFIG.andere;
  const StatusIcon = status.icon;
  const hasContact = report.contact_info?.email;

  return (
    <div className="min-h-screen bg-[#F7FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin/dashboard"
              className="flex items-center gap-2 text-[#718096] hover:text-[#2D3748] transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Zurück
            </Link>
            <div className="hidden md:flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="mobbing-melden.de Logo" 
                className="h-12 w-auto"
              />
              <span className="font-bold text-[#2D3748]" style={{ fontFamily: 'Nunito' }}>
                Fall #{id.substring(0, 8)}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              onClick={exportPDF}
              disabled={isExporting}
              className="btn-outline flex items-center gap-2"
              data-testid="export-pdf-button"
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              PDF exportieren
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-[#718096] hover:text-[#2D3748]"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-6 md:p-8">
        <div className="grid lg:grid-cols-3 gap-6">
          
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Report Details Card */}
            <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="report-details">
              <div className="flex items-center gap-3 mb-6">
                <span 
                  className="px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2"
                  style={{ backgroundColor: status.bgColor, color: status.color }}
                >
                  <StatusIcon className="w-4 h-4" />
                  {status.label}
                </span>
                <span 
                  className="px-4 py-2 rounded-full text-sm font-medium"
                  style={{ backgroundColor: `${category.color}30`, color: category.color }}
                >
                  {category.label}
                </span>
              </div>

              <h2 
                className="text-xl font-bold text-[#2D3748] mb-4"
                style={{ fontFamily: 'Nunito' }}
              >
                Beschreibung
              </h2>
              <p className="text-[#718096] whitespace-pre-wrap leading-relaxed mb-6">
                {report.description}
              </p>

              <div className="flex items-center gap-4 text-sm text-[#A0AEC0] pt-4 border-t border-gray-100">
                <span>Erstellt: {formatDate(report.created_at)}</span>
                <span>Aktualisiert: {formatDate(report.updated_at)}</span>
              </div>
            </div>

            {/* Files Card */}
            {files && files.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="files-section">
                <h2 
                  className="text-xl font-bold text-[#2D3748] mb-4 flex items-center gap-2"
                  style={{ fontFamily: 'Nunito' }}
                >
                  <FileText className="w-5 h-5" />
                  Anhänge ({files.length})
                </h2>
                
                <div className="grid gap-3">
                  {files.map((file) => (
                    <div 
                      key={file.id}
                      className="flex items-center justify-between bg-gray-50 rounded-xl p-4"
                      data-testid={`file-${file.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {file.content_type?.startsWith('image/') ? (
                          <FileImage className="w-5 h-5 text-[#BDE0FE]" />
                        ) : (
                          <FileVideo className="w-5 h-5 text-[#FFC4D6]" />
                        )}
                        <div>
                          <p className="font-medium text-[#2D3748]">{file.filename}</p>
                          <p className="text-xs text-[#A0AEC0]">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {file.content_type?.startsWith('image/') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadFilePreview(file.id)}
                            className="text-[#718096]"
                          >
                            Vorschau
                          </Button>
                        )}
                        <a 
                          href={`${API}/admin/files/${file.id}`}
                          onClick={async (e) => {
                            e.preventDefault();
                            try {
                              const res = await axios.get(`${API}/admin/files/${file.id}`, {
                                headers: { Authorization: `Bearer ${token}` },
                                responseType: 'blob'
                              });
                              const url = window.URL.createObjectURL(new Blob([res.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', file.filename);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                            } catch (err) {
                              toast.error("Download fehlgeschlagen");
                            }
                          }}
                        >
                          <Button variant="ghost" size="sm" className="text-[#A0E8AF]">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reply Section */}
            {hasContact && (
              <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="reply-section">
                <h2 
                  className="text-xl font-bold text-[#2D3748] mb-4 flex items-center gap-2"
                  style={{ fontFamily: 'Nunito' }}
                >
                  <MessageCircle className="w-5 h-5" />
                  Kommunikation
                </h2>

                {/* Previous Replies */}
                {replies && replies.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {replies.map((reply) => (
                      <div 
                        key={reply.id}
                        className={`p-4 rounded-xl ${
                          reply.sender === 'admin' 
                            ? 'bg-[#A0E8AF]/10 ml-8' 
                            : 'bg-gray-50 mr-8'
                        }`}
                      >
                        <p className="text-[#2D3748]">{reply.message}</p>
                        <p className="text-xs text-[#A0AEC0] mt-2">
                          {reply.sender === 'admin' ? 'Admin' : 'Nutzer'} • {formatDate(reply.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* New Reply */}
                <div className="space-y-4">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Schreibe eine Nachricht an den Nutzer..."
                    className="input-base w-full h-32 resize-none"
                    data-testid="reply-textarea"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={isSendingReply || !replyMessage.trim()}
                    className="btn-primary"
                    data-testid="send-reply-button"
                  >
                    {isSendingReply ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Nachricht speichern
                  </Button>
                  <p className="text-xs text-[#A0AEC0]">
                    * In der Produktionsversion wird eine E-Mail an {report.contact_info.email} gesendet
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* Status Update */}
            <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="status-update">
              <h3 
                className="font-bold text-[#2D3748] mb-4"
                style={{ fontFamily: 'Nunito' }}
              >
                Status ändern
              </h3>
              <div className="space-y-2">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  const isActive = report.status === key;
                  return (
                    <button
                      key={key}
                      onClick={() => !isActive && updateStatus(key)}
                      disabled={isUpdating || isActive}
                      className={`w-full p-3 rounded-xl text-left flex items-center gap-3 transition-all ${
                        isActive 
                          ? 'ring-2 ring-offset-2' 
                          : 'hover:bg-gray-50'
                      }`}
                      style={isActive ? { 
                        backgroundColor: config.bgColor, 
                        ringColor: config.color 
                      } : {}}
                      data-testid={`status-${key}`}
                    >
                      <Icon className="w-4 h-4" style={{ color: config.color }} />
                      <span className="font-medium" style={{ color: isActive ? config.color : '#718096' }}>
                        {config.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="contact-info">
              <h3 
                className="font-bold text-[#2D3748] mb-4"
                style={{ fontFamily: 'Nunito' }}
              >
                Kontaktdaten
              </h3>
              {hasContact ? (
                <div className="space-y-3">
                  {report.contact_info.name && (
                    <div className="flex items-center gap-3 text-[#718096]">
                      <User className="w-4 h-4" />
                      <span>{report.contact_info.name}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-[#718096]">
                    <Mail className="w-4 h-4" />
                    <span>{report.contact_info.email}</span>
                  </div>
                  {report.contact_info.phone && (
                    <div className="flex items-center gap-3 text-[#718096]">
                      <Phone className="w-4 h-4" />
                      <span>{report.contact_info.phone}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[#A0AEC0] text-sm">
                  Anonym - keine Kontaktdaten angegeben
                </p>
              )}
            </div>

            {/* Internal Notes */}
            <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="notes-section">
              <h3 
                className="font-bold text-[#2D3748] mb-4"
                style={{ fontFamily: 'Nunito' }}
              >
                Interne Notizen
              </h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notizen zu diesem Fall..."
                className="input-base w-full h-32 resize-none mb-4"
                data-testid="notes-textarea"
              />
              <Button
                onClick={saveNotes}
                disabled={isUpdating}
                variant="outline"
                className="w-full rounded-xl"
                data-testid="save-notes-button"
              >
                {isUpdating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Notizen speichern
              </Button>
            </div>
          </div>
        </div>
      </main>

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="max-w-4xl max-h-[90vh] bg-white rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <span className="font-medium text-[#2D3748]">{previewImage.filename}</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setPreviewImage(null)}
              >
                Schließen
              </Button>
            </div>
            <img 
              src={`data:${previewImage.content_type};base64,${previewImage.data}`}
              alt={previewImage.filename}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}
